import { KpiCard, ErrorState } from '@/components/app';
import { Card } from '@/components/ui/card';
import type { DashboardStats } from '@/api/users';
import { percentDelta } from '@/lib/chart';

interface KPICardsProps {
  stats: DashboardStats | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  /** Окно «новых пользователей», дней (из переключателя периода). */
  periodDays: number;
}

const fmt = (n: number) => new Intl.NumberFormat('ru-RU').format(n);

/**
 * Ряд KPI по макету. Только то, что отдаёт GET /users/dashboard-stats:
 * - выручка за всё время (успешные платежи) — hero, без дельты (API не даёт прошлый период);
 * - DAU сегодня к вчера (activeUsersByPeriod[day]);
 * - с подпиской — доля от всех пользователей;
 * - новые пользователи за период; дельта к предыдущему такому же окну — только если оно
 *   целиком есть в 30-дневном ряду (7 и 14 дней).
 */
export default function KPICards({ stats, loading, error, onRetry, periodDays }: KPICardsProps) {
  if (error) {
    return (
      <Card>
        <ErrorState message={error} onRetry={onRetry} className="py-8" />
      </Card>
    );
  }

  const daily = stats?.dailyActiveUsers ?? [];
  const sum = (rows: typeof daily) => rows.reduce((acc, d) => acc + d.registrations, 0);
  const current = daily.slice(-periodDays);
  const previous = daily.length >= periodDays * 2 ? daily.slice(-periodDays * 2, -periodDays) : [];
  const newUsers = sum(current);
  const newUsersDelta = previous.length ? percentDelta(newUsers, sum(previous)) : null;

  const dau = stats?.activeUsersByPeriod[0];
  const dauDelta = dau ? percentDelta(dau.current, dau.previous) : null;

  const subscribedShare =
    stats && stats.totalUsers > 0
      ? new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format((stats.subscribedUsers / stats.totalUsers) * 100)
      : null;

  return (
    <section aria-label="Ключевые показатели" className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <KpiCard
        variant="hero"
        loading={loading}
        label="Выручка за всё время"
        value={stats ? fmt(stats.totalRevenue) : '—'}
        unit="сум"
        hint="Сумма успешных платежей"
      />
      <KpiCard
        loading={loading}
        label="Активны сегодня (DAU)"
        value={dau ? fmt(dau.current) : '—'}
        delta={dauDelta ? { text: `${dauDelta.text} к вчера`, tone: dauDelta.tone } : undefined}
        hint={!dauDelta && dau ? `Вчера: ${fmt(dau.previous)}` : undefined}
      />
      <KpiCard
        loading={loading}
        label="С подпиской"
        value={stats ? fmt(stats.subscribedUsers) : '—'}
        hint={subscribedShare !== null ? `${subscribedShare}% от всех пользователей` : undefined}
      />
      <KpiCard
        loading={loading}
        label={`Новые пользователи за ${periodDays} дней`}
        value={stats ? fmt(newUsers) : '—'}
        delta={
          newUsersDelta
            ? { text: `${newUsersDelta.text} к прошлым ${periodDays} дням`, tone: newUsersDelta.tone }
            : undefined
        }
        hint={stats ? `Всего пользователей: ${fmt(stats.totalUsers)}` : undefined}
      />
    </section>
  );
}
